<template>
  <div align="center" class="wrapper">
    <ContactUsHeader />
    <spinner v-if="loadingRedirection"></spinner>
    <success v-if="isSuccessModalVisible"></success>
    <div class="block-center mt-xl wd-xl">
      <!-- START panel-->
      <div class="panel panel-dark panel-flat">

        <!-- STEP 1: Email + Password Entry -->
        <div class="panel-body" v-if="loginStep === 'login' && !needsMfa">
          <p class="text-center login-intro-desktop">
            Log in to your account below |
            <router-link :to="{ name: 'Registration' }">First time user?</router-link>
          </p>
          <div class="login-intro-mobile">
            <p class="text-center login-intro-main">Log in to your account below</p>
            <p class="text-center login-intro-link">
              <router-link :to="{ name: 'Registration' }">First time user?</router-link>
            </p>
          </div>
          <div v-if="migrationNeeded" class="alert alert-info text-center">
            We've updated our system. Please
            <router-link :to="{ name: 'ResetPassword' }">Reset Password</router-link>
            to access your account.
          </div>
          <strong>
            <p class="text-red">{{ otherErrors }}</p>
          </strong>
          <p v-if="isUserIdle"><strong class="text-red">You have been logged out of your account due to
              inactivity.</strong></p>
          <p v-if="fromPasswordResetEmail"><strong class="text-success">Your password has been reset
              successfully.</strong></p>

          <form role="form" class="mb-lg" @submit.prevent="handleLoginSubmit()" novalidate>
            <div class="form-group has-feedback" v-bind:class="{ 'has-error': emailError }">
              <input v-model="computedEmail" id="signupInputEmail1" type="email" placeholder="Enter email"
                autocomplete="off" required class="form-control">
              <em class="form-icon icon-envelope"></em>
              <span id="helpBlock2" class="help-block">{{ emailError }}</span>
            </div>

            <div class="form-group has-feedback" v-bind:class="{ 'has-error': passwordError }">
              <input v-model="password" id="signupInputPassword1" type="password" placeholder="Password"
                autocomplete="off" required class="form-control">
              <em class="form-icon icon-lock"></em>
              <span id="helpBlock4" class="help-block">{{ passwordError }}</span>
            </div>

            <div class="remember-forgot-container">
              <div class="checkbox c-checkbox mt0">
                <label>
                  <input type="checkbox" value="" name="rememberMe" v-model="rememberMe">
                  <span style="background-color: white;">
                    <icon v-show="rememberMe" name="check"></icon>
                  </span>Remember Me</label>
              </div>
              <div class="forgot-password-link">
                <router-link class="text-muted" :to="{ name: 'ResetPassword' }">Forgot your password?</router-link>
              </div>
            </div>

            <button type="submit" class="btn btn-block btn-default btn-lg mt-lg"
              :disabled="submittedForm || loadingMfaOptions">
              {{ loadingMfaOptions ? 'Loading...' : 'Log in' }}
            </button>

            <div class="form-group has-feedback" v-bind:class="{ 'has-error': loginError }">
              <strong id="helpBlock6" class="help-block">{{ loginError }}</strong>
            </div>
          </form>
        </div>

        <!-- STEP 2: Method Selection -->
        <div class="panel-body" v-if="loginStep === 'method'">
          <p class="text-center pv"><strong>Choose verification method</strong></p>
          <strong>
            <p class="text-red">{{ otherErrors }}</p>
          </strong>

          <form role="form" class="mb-lg" @submit.prevent="handleMethodSubmit()" novalidate>
            <!-- Method Selection -->
            <div class="method-selector mb-lg">
              <label class="method-option" v-if="mfaOptions && mfaOptions.emailAvailable">
                <input type="radio" name="method" value="EMAIL" v-model="selectedMethod">
                <span class="method-text">
                  <strong>Email</strong><br>
                  <small>Send code to {{ mfaOptions.email }}</small>
                </span>
              </label>

            <label class="method-option" v-if="mfaOptions && mfaOptions.smsAvailable">
              <input type="radio" name="method" value="SMS" v-model="selectedMethod">
              <span class="method-text">
                <strong>Text Message</strong><br>
                <small>Send code to {{ mfaOptions.mfaPhonePreference === 'SECONDARY' ? mfaOptions.secondaryPhone : mfaOptions.phone }}</small>
              </span>
            </label>
            </div>

            <button type="submit" class="btn btn-block btn-default btn-lg mt-lg" :disabled="submittedForm">
              {{ submittedForm ? 'Sending...' : 'Send Code' }}
            </button>

            <p class="pt-lg text-center border-top mt-lg">
              <a v-on:click="backToLogin" class="cursor-pointer">Back</a>
            </p>
          </form>
        </div>

        <!-- STEP 3: Code Verification -->
        <div class="panel-body" v-if="loginStep === 'code' && needsMfa">
          <p class="text-center pv"><strong>Enter verification code</strong></p>
          <p class="text-center">
            Code sent to <strong>{{ sentTo }}</strong> via {{ selectedMethod === 'SMS' ? 'text message' : 'email' }}
          </p>
          <strong>
            <p class="text-red">{{ otherErrors }}</p>
          </strong>

          <form role="form" class="mb-lg" @submit.prevent="confirmCognitoMfaCode()" novalidate>
            <input v-model="mfaCode" id="mfaCode" type="text" placeholder="Enter code" autocomplete="off" required
              maxlength="6" class="form-control code-input">

            <button type="submit" class="btn btn-block btn-default btn-lg mt-lg"
              :disabled="submittedMfa || mfaCode.length !== 6">
              {{ submittedMfa ? 'Verifying...' : 'Verify' }}
            </button>
          </form>

          <!-- Resend Options -->
          <div class="resend-options">
            <p class="text-muted">Didn't receive the code?</p>

            <button v-if="selectedMethod === 'EMAIL' && mfaOptions && mfaOptions.smsAvailable"
              type="button"
              @click="resendWithDifferentMethod('SMS')"
              :disabled="submittedMfa"
              class="link-button">
              Send via text message instead
            </button>

            <button v-if="selectedMethod === 'SMS' && mfaOptions && mfaOptions.emailAvailable"
              type="button"
              @click="resendWithDifferentMethod('EMAIL')"
              :disabled="submittedMfa"
              class="link-button">
              Send via email instead
            </button>

            <button type="button" @click="resendCurrentMethod()" :disabled="submittedMfa" class="link-button">
              Resend code
            </button>
          </div>

          <p class="pt-lg text-center border-top mt-lg">
            <a v-on:click="backToMethodSelection" class="cursor-pointer">Back</a>
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import { isEmpty } from 'lodash';

import {
  SET_ACCOUNT_STATUS,
  SET_ACTIVE_ACCOUNT_ID,
  SET_IDLE_OFF,
  SET_NEW_USER_EMAIL,
  SET_REDIRECT_URL,
  SET_USER,
} from '@/store/mutation-types';

import success from '@/components/modals/Success';
import spinner from '@/components/widgets/Spinner';
import ContactUsHeader from '@/components/ContactUsHeader';
import { logAction, logglyTags } from '@/util';
import logActions from '@/data/static/logActions';
import emailErrorsTypes from '@/data/static/emailErrorsTypes';

export default {
  name: 'login',
  created() {
    const { email, success: successMsg, message } = this.$route.query;
    if (email) {
      this.email = email;
    }
    if (successMsg === false) {
      this.$router.push({ name: 'ErrorAuth', query: { email, message, success: successMsg, type: emailErrorsTypes.CONFIRMATION_EMAIL.name } });
    }
  },
  beforeMount() {
    const email = localStorage.getItem('email');
    this.setRedirectUrl(this.$route.query.redirectUrl);
    if (email) {
      this.email = email;
      this.rememberMe = true;
    }
  },
  data() {
    return {
      // Form fields
      email: '',
      emailError: '',
      password: '',
      passwordError: '',
      mfaCode: '',
      showPassword: false,
      loginError: '',
      otherErrors: '',
      rememberMe: false,

      // MFA flow state
      loginStep: 'login', // 'login' | 'method' | 'code'
      needsMfa: false,
      mfaOptions: null,
      selectedMethod: 'EMAIL',
      initialMethodUsed: null, // Track which method was used when first code was sent
      sentTo: '',

      // Loading states
      submittedForm: false,
      submittedMfa: false,
      loadingMfaOptions: false,

      // Other
      migrationNeeded: false,
    };
  },
  computed: {
    ...mapState('authentication', [
      'isUserIdle',
      'redirectUrl',
    ]),
    fromPasswordResetEmail() {
      return !!this.$route.query.resetEmail;
    },
    ...mapState('success', [
      'isSuccessModalVisible',
    ]),
    ...mapState('app', [
      'accountTypes',
      'activeAccount',
      'irAdmin',
      'userTypes',
      'loadingRedirection',
    ]),
    computedEmail: {
      get() {
        return this.email;
      },
      set(value) {
        this.email = value.toLowerCase();
      },
    },
  },
  components: {
    success,
    spinner,
    ContactUsHeader,
  },
  methods: {
    ...mapMutations('app', {
      setUser: SET_USER,
      setAccountStatus: SET_ACCOUNT_STATUS,
      setActiveAccountId: SET_ACTIVE_ACCOUNT_ID,
    }),
    ...mapMutations('user', {
      setNewUserEmail: SET_NEW_USER_EMAIL,
    }),
    ...mapMutations('authentication', {
      setRedirectUrl: SET_REDIRECT_URL,
      setIdleOff: SET_IDLE_OFF,
    }),
    ...mapActions('app', [
      'getStaticInformation',
    ]),
    ...mapActions('authentication', [
      'logIn',
      'handleMfa',
      'legacyLogin',
      'verifyMigration',
    ]),
    ...mapActions('accountCreation', [
      'getUserProfile',
    ]),
    backToLogin() {
      this.loginStep = 'login';
      this.needsMfa = false;
      this.submittedForm = false;
      this.submittedMfa = false;
      this.otherErrors = '';
      this.mfaCode = '';
      this.mfaOptions = null;
      this.selectedMethod = 'EMAIL';
      this.initialMethodUsed = null;
      this.sentTo = '';
      this.password = '';
    },
    backToMethodSelection() {
      this.loginStep = 'method';
      this.mfaCode = '';
      this.otherErrors = '';
      this.submittedMfa = false;
    },
    resetValidations() {
      this.emailError = '';
      this.passwordError = '';
      this.otherErrors = '';
    },
    async handleLoginSubmit() {
      this.submittedForm = true;
      this.resetValidations();

      if (!this.validateEmail(this.email) || !this.validatePassword(this.password)) {
        this.submittedForm = false;
        return;
      }

      // First, validate password by attempting login
      // This will catch incorrect passwords immediately before MFA flow
      try {
        // Attempt login with DEFER to validate password without sending a code
        // User will choose their preferred method on the next screen
        const result = await this.$cognitoAuth.login(
          this.email.toLowerCase(),
          this.password,
          'DEFER',
        );

        // Password is correct - proceed with MFA flow if needed
        if (result.status === 'SUCCESS') {
          // No MFA required - login directly
          this.continueLogin(this.email);
          this.submittedForm = false;
        } else if (result.status === 'NEEDS_MFA') {
          // MFA required - get options and proceed with MFA flow
          this.loadingMfaOptions = true;

          try {
            // Get MFA options from backend
            const options = await this.$http.getMfaOptions(this.email.toLowerCase());
            this.mfaOptions = options;

            // DEFER mode: no code was sent yet, user will choose method
            this.initialMethodUsed = null;

            // If user has NONE preference, skip SMS and use email only
            const prefersEmailOnly = options.mfaPhonePreference === 'NONE' || !options.smsAvailable;

            // Pre-select based on user's preference (default to EMAIL)
            if (prefersEmailOnly) {
              this.selectedMethod = 'EMAIL';
            } else if (options.mfaPhonePreference === 'PRIMARY' || options.mfaPhonePreference === 'SECONDARY') {
              this.selectedMethod = 'SMS';
            } else {
              this.selectedMethod = 'EMAIL';
            }

            // Check if user has both methods available
            const hasBothMethods = options.emailAvailable &&
              options.smsAvailable &&
              !prefersEmailOnly;

            if (hasBothMethods) {
              // User has both methods - show method selection (no code sent yet)
              this.loginStep = 'method';
              this.submittedForm = false;
              this.loadingMfaOptions = false;
            } else {
              // User has only one method - send code now via that method
              // eslint-disable-next-line no-nested-ternary
              const methodToUse = prefersEmailOnly ? 'EMAIL' : (options.smsAvailable ? 'SMS' : 'EMAIL');

              try {
                const sendResult = await this.$cognitoAuth.login(
                  this.email.toLowerCase(),
                  this.password,
                  methodToUse,
                );

                if (sendResult.status === 'NEEDS_MFA') {
                  this.selectedMethod = methodToUse;
                  this.initialMethodUsed = methodToUse;

                  if (methodToUse === 'SMS') {
                    if (options.mfaPhonePreference === 'SECONDARY') {
                      this.sentTo = options.secondaryPhone || options.phone;
                    } else {
                      this.sentTo = options.phone;
                    }
                  } else {
                    this.sentTo = options.email;
                  }

                  this.loginStep = 'code';
                  this.needsMfa = true;
                }
              } catch (sendError) {
                console.error('Error sending MFA code:', sendError);
                this.otherErrors = 'Failed to send verification code';
              }

              this.submittedForm = false;
              this.loadingMfaOptions = false;
            }
          } catch (optionsError) {
            // If MFA options API fails, default to EMAIL and send code
            this.mfaOptions = {
              emailAvailable: true,
              smsAvailable: false,
              email: this.email.toLowerCase(),
              phone: null,
            };

            try {
              const sendResult = await this.$cognitoAuth.login(
                this.email.toLowerCase(),
                this.password,
                'EMAIL',
              );

              if (sendResult.status === 'NEEDS_MFA') {
                this.selectedMethod = 'EMAIL';
                this.initialMethodUsed = 'EMAIL';
                this.sentTo = this.email.toLowerCase();
                this.loginStep = 'code';
                this.needsMfa = true;
              }
            } catch (sendError) {
              console.error('Error sending MFA code:', sendError);
              this.otherErrors = 'Failed to send verification code';
            }

            this.submittedForm = false;
            this.loadingMfaOptions = false;
          }
        }
      } catch (error) {
        // Password validation failed - show error immediately
        if (error.code === 'NotAuthorizedException') {
          this.otherErrors = 'Incorrect password';
        } else {
          this.otherErrors = error.message || 'Login failed';
        }
        this.submittedForm = false;
      }
    },

    async handleMethodSubmit() {
      this.submittedForm = true;
      this.resetValidations();

      // Send code via selected method (DEFER mode means no code was sent yet)
      try {
        const result = await this.$cognitoAuth.login(
          this.email.toLowerCase(),
          this.password,
          this.selectedMethod,
        );

        if (result.status === 'NEEDS_MFA') {
          // Track which method was used
          this.initialMethodUsed = this.selectedMethod;

          // Set destination display
          if (this.selectedMethod === 'SMS') {
            if (this.mfaOptions.mfaPhonePreference === 'SECONDARY') {
              this.sentTo = this.mfaOptions.secondaryPhone || this.mfaOptions.phone;
            } else {
              this.sentTo = this.mfaOptions.phone;
            }
          } else {
            this.sentTo = this.mfaOptions.email;
          }

          this.loginStep = 'code';
          this.needsMfa = true;
        } else if (result.status === 'SUCCESS') {
          // Success without MFA
          this.continueLogin(this.email);
        }
      } catch (error) {
        if (error.code === 'NotAuthorizedException') {
          this.otherErrors = 'Incorrect password';
        } else {
          this.otherErrors = error.message || 'Login failed';
        }
      } finally {
        this.submittedForm = false;
      }
    },

    async confirmCognitoMfaCode() {
      this.submittedMfa = true;
      const { mfaCode } = this;

      if (!mfaCode || mfaCode.length !== 6) {
        this.otherErrors = 'Please enter a valid 6-character code';
        this.submittedMfa = false;
        return;
      }

      try {
        await this.$cognitoAuth.handleMfa(mfaCode);
        this.mfaCode = '';
        this.submittedMfa = false;
        this.needsMfa = false;
        this.continueLogin(this.email);
      } catch (error) {
        this.otherErrors = 'Invalid verification code';
        this.mfaCode = '';
        this.submittedMfa = false;
      }
    },

    async resendWithDifferentMethod(newMethod) {
      this.submittedMfa = true;
      this.otherErrors = '';
      this.mfaCode = '';

      try {
        const result = await this.$cognitoAuth.resendCode(newMethod);

        if (result.status === 'NEEDS_MFA') {
          this.selectedMethod = newMethod;
          this.initialMethodUsed = newMethod;

          // Update destination display
          if (newMethod === 'SMS') {
            if (this.mfaOptions.mfaPhonePreference === 'SECONDARY') {
              this.sentTo = this.mfaOptions.secondaryPhone || this.mfaOptions.phone;
            } else {
              this.sentTo = this.mfaOptions.phone;
            }
          } else {
            this.sentTo = this.mfaOptions.email;
          }
        }
      } catch (error) {
        this.otherErrors = error.message || 'Failed to resend code';
      } finally {
        this.submittedMfa = false;
      }
    },

    async resendCurrentMethod() {
      await this.resendWithDifferentMethod(this.selectedMethod);
    },
    validateMfaAndSubmitLogin() {
      this.submittedMfa = true;
      const { mfaCode } = this;
      if (mfaCode) {
        this.handleMfa({ mfaCode })
          .then(() => {
            this.continueLogin(this.email);
          })
          .catch((error) => {
            this.submittedMfa = false;
            this.otherErrors = error.error_description
              .replace('binding_code', 'code')
              .replace('mfa_token', 'Code');
          });
      }
    },
    continueLogin(email) {
      const emailToSave = this.rememberMe ? this.email : '';
      localStorage.setItem('email', emailToSave);
      this.getUserProfile().then((user) => {
        logAction({
          user: user.userId,
          level: 'info',
          action: logActions.LOGIN.name,
          message: `User logged in ${user.userId} ${email}`,
          tags: [logglyTags.auth0],
        });
        this.setUser(user);
        this.completeLogin(user);
      });
    },

    completeLogin(user) {
      this.checkForAccountStatus(user);
      if (this.irAdmin) {
        this.$router.push({ name: 'Users' });
      } else {
        if (this.redirectUrl && this.redirectUrl.includes('funding-sources')) {
          this.$router.push(this.redirectUrl);
          return;
        }
        this.$router.push({ name: 'AccountSummary' });
      }
      window.dataLayer.push({
        event: 'login',
        userID: user.userId || `new_user:${this.email}`,
      });
      this.setNewUserEmail(this.email);
      this.submittedForm = false;
      this.submittedMfa = false;
    },

    checkForAccountStatus(userInformation) {
      let account = {};
      const doesUserHaveAccounts = !!(userInformation.accounts && userInformation.accounts.length);

      if (doesUserHaveAccounts) {
        account = userInformation.accounts.find(
          a => a.id === userInformation.defaultAccountId,
        );
      }
      if (isEmpty(account)) {
        account = {
          accountName: `${userInformation.firstName} ${userInformation.lastName}`,
          id: 0,
          accountType: userInformation.userType,
          status: undefined,
        };
        if (account.accountType !== 'ADMIN') {
          account.accountType = 'INDIVIDUAL';
        }
      }

      this.setActiveAccountId(account);
      this.setAccountStatus(account.status);
    },
    validateEmail(email) {
      const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line no-useless-escape
      if (!regex.test(email)) {
        this.emailError = 'Please, enter a valid email';
        return false;
      }
      return true;
    },
    validatePassword(password) {
      if (password.length < 1) {
        this.passwordError = 'Please enter a password';
        return false;
      }
      return true;
    },
  },
};
</script>

<style>
body {
  background-color: #fff;
}

/* Global mobile overrides - must be non-scoped to override theme.scss */
@media (max-width: 1024px) {
  .wrapper {
    padding: 0 !important;
    margin: 0 !important;
    align-items: stretch !important;
  }

  .wrapper[align="center"] {
    display: flex !important;
    flex-direction: column !important;
    min-height: 100vh !important;
  }

  .block-center.mt-xl {
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .block-center.mt-xl.wd-xl {
    margin: 0 !important;
    width: 100% !important;
  }

  .panel-dark.panel-flat {
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    flex: 1 !important;
  }
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.wd-xl {
  width: 360px;
  max-width: 100%;
}

.text-red {
  color: #a94442;
}

.panel-body {
  padding: 15px 25px;
}

.form-icon {
  position: absolute;
  top: 10px;
  right: 12px;
}

.cursor-pointer {
  cursor: pointer;
}

.login-intro-desktop {
  line-height: 1.6;
  display: block;
}

.login-intro-mobile {
  display: none;
}

.login-intro-main {
  line-height: 1.6;
  margin-bottom: 8px;
}

.login-intro-link {
  margin-top: 8px;
  margin-bottom: 0;
}

/* Remember me and forgot password container */
.remember-forgot-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.forgot-password-link {
  text-align: right;
}

/* MFA Method Selector Styles */
.method-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.method-option {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0;
}

.method-option:hover {
  border-color: #4A90E2;
  background-color: #f8f9fa;
}

.method-option input[type="radio"] {
  margin-right: 12px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.method-option input[type="radio"]:checked {
  accent-color: #4A90E2;
}

.method-option:has(input[type="radio"]:checked) {
  border-color: #4A90E2;
  background-color: #f0f7ff;
}

.method-text {
  flex: 1;
  word-break: break-word;
}

.method-text strong {
  color: #333;
  font-size: 16px;
}

.method-text small {
  color: #666;
  font-size: 13px;
  display: block;
  margin-top: 4px;
}

/* Code Input Styles */
.code-input {
  text-align: center;
  font-size: 24px;
  letter-spacing: 6px;
  font-family: 'Courier New', Courier, monospace;
  font-weight: 500;
}

/* Resend Options Styles */
.resend-options {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  text-align: center;
}

.resend-options p {
  color: #666;
  margin-bottom: 12px;
  font-size: 14px;
}

.link-button {
  background: none;
  border: none;
  color: #4A90E2;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  margin: 4px;
  font-size: 14px;
  display: inline-block;
}

.link-button:hover:not(:disabled) {
  color: #357ABD;
}

.link-button:disabled {
  color: #ccc;
  cursor: not-allowed;
  text-decoration: none;
}

.text-muted {
  color: #666;
}

.mb-lg {
  margin-bottom: 20px;
}

.mt-lg {
  margin-top: 20px;
}

/* Mobile responsive styles */
@media (max-width: 1024px) {
  .panel-body {
    padding: 30px 24px !important;
  }

  .login-intro-desktop {
    display: none !important;
  }

  .login-intro-mobile {
    display: block !important;
    margin-bottom: 24px;
  }

  .login-intro-main {
    font-size: 18px;
    line-height: 1.6;
    margin-bottom: 12px;
    margin-top: 0;
  }

  .login-intro-link {
    font-size: 18px;
    margin-top: 0;
    margin-bottom: 0;
  }

  .login-intro-link a {
    font-size: 18px;
    text-decoration: none;
    color: #4A90E2;
    font-weight: 500;
  }

  .form-control {
    font-size: 18px !important;
    height: 50px !important;
    padding: 12px 16px !important;
  }

  .form-icon {
    font-size: 18px !important;
    top: 15px !important;
    right: 16px !important;
  }

  .remember-forgot-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
    margin-top: 16px;
  }

  .checkbox label {
    font-size: 16px !important;
  }

  .forgot-password-link {
    text-align: left;
    width: 100%;
  }

  .forgot-password-link a {
    font-size: 16px !important;
  }

  .method-option {
    padding: 20px 16px;
  }

  .method-text strong {
    font-size: 18px;
  }

  .method-text small {
    font-size: 15px;
    margin-top: 6px;
  }

  .code-input {
    font-size: 28px;
    letter-spacing: 8px;
    padding: 16px 12px;
    height: 60px;
  }

  .link-button {
    display: block;
    width: 100%;
    margin: 10px 0;
    padding: 16px 12px;
    text-align: center;
    font-size: 16px;
  }

  .resend-options {
    padding-top: 16px;
    margin-top: 16px;
  }

  .resend-options p {
    margin-bottom: 12px;
    font-size: 16px;
  }

  .btn-lg {
    font-size: 20px !important;
    padding: 16px 24px !important;
    height: auto !important;
  }

  .mt-lg {
    margin-top: 20px;
  }

  .mb-lg {
    margin-bottom: 0;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .help-block {
    font-size: 15px;
  }

  .alert {
    margin-bottom: 16px;
    font-size: 15px;
    padding: 12px;
  }

  p {
    margin-bottom: 12px;
    margin-top: 12px;
    font-size: 16px;
  }

  p:first-child {
    margin-top: 0;
  }

  p.text-center {
    margin-bottom: 12px;
  }

  p strong {
    font-size: 16px;
  }

  .text-red,
  .text-success {
    font-size: 16px;
  }

  .pv {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .pt-lg {
    padding-top: 18px !important;
  }

  .border-top {
    margin-top: 18px !important;
  }
}

@media (max-width: 480px) {
  .panel-body {
    padding: 24px 20px !important;
  }

  .login-intro-desktop {
    display: none !important;
  }

  .login-intro-mobile {
    display: block !important;
  }

  .login-intro-main {
    font-size: 17px;
  }

  .login-intro-link {
    font-size: 17px;
  }

  .login-intro-link a {
    font-size: 17px;
  }

  .form-control {
    font-size: 17px !important;
    height: 48px !important;
  }

  .form-icon {
    font-size: 17px !important;
    top: 14px !important;
  }

  .method-option {
    padding: 18px 14px;
  }

  .method-text strong {
    font-size: 17px;
  }

  .method-text small {
    font-size: 14px;
  }

  .code-input {
    font-size: 26px;
    letter-spacing: 6px;
    padding: 14px 10px;
    height: 56px;
  }

  .link-button {
    font-size: 16px;
    padding: 15px 10px;
  }

  .btn-lg {
    font-size: 19px !important;
    padding: 15px 22px !important;
  }
}

/* Extra small devices */
@media (max-width: 360px) {
  .panel-body {
    padding: 20px 16px !important;
  }

  .login-intro-desktop {
    display: none !important;
  }

  .login-intro-mobile {
    display: block !important;
  }

  .login-intro-main {
    font-size: 16px;
  }

  .login-intro-link {
    font-size: 16px;
  }

  .login-intro-link a {
    font-size: 16px;
  }

  .form-control {
    font-size: 16px !important;
    height: 46px !important;
  }

  .method-option {
    padding: 16px 12px;
  }

  .method-text strong {
    font-size: 16px;
  }

  .method-text small {
    font-size: 13px;
    line-height: 1.4;
  }

  .code-input {
    font-size: 24px;
    letter-spacing: 5px;
    height: 52px;
  }

  .btn-lg {
    font-size: 18px !important;
    padding: 14px 20px !important;
  }
}
</style>



// WEBPACK FOOTER //
// Login.vue?15003ac6